/**
 * Who is working right now. Every open CRM sends a heartbeat each minute
 * (gula_presence_ping): admins see who is online, idle or away, how long each
 * one worked in every call slot today, and a warning when a call slot has
 * nobody calling.
 */

export type PresenceState = "calling" | "online" | "idle" | "offline";

export interface PresenceRow {
  sales_id: number;
  last_seen_at: string;
  last_active_at?: string | null;
  calling_since?: string | null;
  device?: "mobile" | "desktop" | null;
}

export interface CallLogRow {
  sales_id: number;
  created_at: string;
  outcome: string;
}

export interface PresenceBlock {
  sales_id: number;
  block_at: string;
}

export interface PresenceWorker {
  id: number | string;
  first_name: string;
  last_name: string;
  administrator?: boolean;
}

/** Heartbeat every minute: no heartbeat for this long = CRM closed. */
export const SEEN_MS = 150_000;
/** No click, tap or key for this long = away from the screen. */
export const ACTIVE_MS = 5 * 60_000;
/** A call logged this recently counts as working (phone in the other hand). */
export const RECENT_CALL_MS = 10 * 60_000;
/** «Llamar por WhatsApp» keeps them as calling at most this long. */
export const CALLING_MS = 20 * 60_000;
/** Minutes into a slot before warning that nobody is calling. */
export const SLOT_GRACE_MIN = 10;
/** Admins count as covering the slot when they called this recently. */
export const ADMIN_CALL_MS = 15 * 60_000;
/** Size of the activity blocks saved by the database. */
export const BLOCK_MIN = 5;

export const SLOTS = [
  { key: "9-14", label: "Mañana", hours: "9 a 14 h", from: 9, to: 14 },
  { key: "14-18", label: "Mediodía", hours: "14 a 18 h", from: 14, to: 18 },
  { key: "18-21", label: "Tarde", hours: "18 a 21 h", from: 18, to: 21 },
] as const;

export type SlotKey = (typeof SLOTS)[number]["key"];

/** Call slot of a moment (team's local time), like currentCallSlot. */
export const slotOfDate = (date: Date): SlotKey | null =>
  SLOTS.find(
    (slot) => date.getHours() >= slot.from && date.getHours() < slot.to,
  )?.key ?? null;

const ms = (value?: string | null) => (value ? Date.parse(value) : NaN);
const within = (value: string | null | undefined, now: number, span: number) =>
  now - ms(value) < span;

/** Last call logged by each person today. */
export const lastCallBySeller = (calls: CallLogRow[]) => {
  const result = new Map<string, number>();
  for (const call of calls) {
    if (call.outcome === "skip") continue;
    const at = ms(call.created_at);
    const key = String(call.sales_id);
    if (!(at <= (result.get(key) ?? -Infinity))) result.set(key, at);
  }
  return result;
};

export const presenceState = (
  row: PresenceRow | undefined,
  lastCallAt: number | undefined,
  now: number,
): PresenceState => {
  if (row?.calling_since && within(row.calling_since, now, CALLING_MS))
    return "calling";
  const seen = !!row && within(row.last_seen_at, now, SEEN_MS);
  const active =
    (!!row && within(row.last_active_at, now, ACTIVE_MS)) ||
    (lastCallAt != null && now - lastCallAt < RECENT_CALL_MS);
  if (active) return "online";
  return seen ? "idle" : "offline";
};

/** "hace 3 min", "hace 1 h 20 min", "a las 9:40" (before today: "el lunes"). */
export const agoLabel = (at: number, now: number): string => {
  const minutes = Math.max(0, Math.round((now - at) / 60_000));
  if (minutes < 1) return "ahora mismo";
  if (minutes < 60) return `hace ${minutes} min`;
  const date = new Date(at);
  const sameDay = new Date(now).toDateString() === date.toDateString();
  if (sameDay && minutes < 180) {
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return `hace ${hours} h${rest ? ` ${rest} min` : ""}`;
  }
  const time = date.toLocaleTimeString("es-ES", {
    hour: "numeric",
    minute: "2-digit",
  });
  if (sameDay) return `a las ${time}`;
  const day = date.toLocaleDateString("es-ES", { weekday: "long" });
  return `el ${day} a las ${time}`;
};

/** "1 h 20 min", "45 min" */
export const durationLabel = (minutes: number): string => {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return `${hours} h${rest ? ` ${rest} min` : ""}`;
};

export interface SlotWork {
  minutes: number;
  calls: number;
}

/** Minutes active and calls made in each slot today, per person. */
export const workBySlot = (
  blocks: PresenceBlock[],
  calls: CallLogRow[],
): Map<string, Record<SlotKey, SlotWork>> => {
  const result = new Map<string, Record<SlotKey, SlotWork>>();
  const entry = (salesId: number) => {
    const key = String(salesId);
    let value = result.get(key);
    if (!value) {
      value = Object.fromEntries(
        SLOTS.map((slot) => [slot.key, { minutes: 0, calls: 0 }]),
      ) as Record<SlotKey, SlotWork>;
      result.set(key, value);
    }
    return value;
  };
  for (const block of blocks) {
    const slot = slotOfDate(new Date(block.block_at));
    if (slot) entry(block.sales_id)[slot].minutes += BLOCK_MIN;
  }
  for (const call of calls) {
    if (call.outcome === "skip") continue;
    const slot = slotOfDate(new Date(call.created_at));
    if (slot) entry(call.sales_id)[slot].calls += 1;
  }
  return result;
};

export interface PresenceLine {
  worker: PresenceWorker;
  state: PresenceState;
  /** Last moment they were working (screen, call or WhatsApp call) */
  lastActiveAt?: number;
  lastSeenAt?: number;
  lastCallAt?: number;
  callsToday: number;
  device?: "mobile" | "desktop" | null;
  slots: Record<SlotKey, SlotWork>;
}

const STATE_ORDER: Record<PresenceState, number> = {
  calling: 0,
  online: 1,
  idle: 2,
  offline: 3,
};

/**
 * One line per person who worked today: setters who opened the CRM or
 * called; admins (Joaquín, Mica) only on the days they make calls.
 */
export const presenceLines = ({
  workers,
  presence,
  blocks,
  calls,
  now,
}: {
  workers: PresenceWorker[];
  presence: PresenceRow[];
  blocks: PresenceBlock[];
  calls: CallLogRow[];
  now: number;
}): PresenceLine[] => {
  const lastCalls = lastCallBySeller(calls);
  const work = workBySlot(blocks, calls);
  const empty = () =>
    Object.fromEntries(
      SLOTS.map((slot) => [slot.key, { minutes: 0, calls: 0 }]),
    ) as Record<SlotKey, SlotWork>;
  return (
    workers
      .map((worker): PresenceLine => {
        const key = String(worker.id);
        const row = presence.find((item) => String(item.sales_id) === key);
        const lastCallAt = lastCalls.get(key);
        const active = [
          ms(row?.last_active_at),
          ms(row?.calling_since),
          lastCallAt,
        ]
          .filter((value): value is number => Number.isFinite(value))
          .sort((a, b) => b - a)[0];
        return {
          worker,
          state: presenceState(row, lastCallAt, now),
          lastActiveAt: active,
          lastSeenAt: Number.isFinite(ms(row?.last_seen_at))
            ? ms(row?.last_seen_at)
            : undefined,
          lastCallAt,
          callsToday: calls.filter(
            (call) => String(call.sales_id) === key && call.outcome !== "skip",
          ).length,
          device: row?.device,
          slots: work.get(key) ?? empty(),
        };
      })
      // Only who worked today: setters who opened the CRM or called,
      // admins only when they called
      .filter((line) =>
        line.worker.administrator
          ? line.callsToday > 0
          : line.callsToday > 0 ||
            [line.lastActiveAt, line.lastSeenAt].some(
              (value) =>
                value != null &&
                new Date(value).toDateString() === new Date(now).toDateString(),
            ),
      )
      .sort(
        (a, b) =>
          STATE_ORDER[a.state] - STATE_ORDER[b.state] ||
          Number(!!a.worker.administrator) - Number(!!b.worker.administrator) ||
          a.worker.first_name.localeCompare(b.worker.first_name, "es"),
      )
  );
};

/** Somebody is covering the calls right now. */
const covering = (line: PresenceLine, now: number) =>
  line.worker.administrator
    ? line.lastCallAt != null && now - line.lastCallAt < ADMIN_CALL_MS
    : line.state === "calling" || line.state === "online";

export interface EmptySlotAlert {
  slot: (typeof SLOTS)[number];
  /** Minutes since the slot started */
  minutesIn: number;
  /** Who worked last and when (today) */
  last?: { name: string; at: number };
  /** Nobody worked at all in this slot today */
  nobodyThisSlot: boolean;
}

/**
 * Warning for Inicio: we are in a call slot (Monday to Saturday), it started
 * at least 10 minutes ago and nobody is calling.
 */
export const emptySlotAlert = (
  lines: PresenceLine[],
  now: number,
): EmptySlotAlert | null => {
  const date = new Date(now);
  if (date.getDay() === 0) return null;
  const slotKey = slotOfDate(date);
  const slot = SLOTS.find((item) => item.key === slotKey);
  if (!slot) return null;
  const minutesIn = (date.getHours() - slot.from) * 60 + date.getMinutes();
  if (minutesIn < SLOT_GRACE_MIN) return null;
  if (lines.some((line) => covering(line, now))) return null;
  const today = date.toDateString();
  const last = lines
    .filter(
      (line) =>
        line.lastActiveAt != null &&
        new Date(line.lastActiveAt).toDateString() === today,
    )
    .sort((a, b) => (b.lastActiveAt ?? 0) - (a.lastActiveAt ?? 0))[0];
  return {
    slot,
    minutesIn,
    last: last
      ? { name: last.worker.first_name, at: last.lastActiveAt as number }
      : undefined,
    nobodyThisSlot: lines.every(
      (line) => !line.slots[slot.key].minutes && !line.slots[slot.key].calls,
    ),
  };
};
